import { useRef, useSyncExternalStore } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';
import { getSnapshot, resolveConfirm, subscribe } from './confirm-store';

export function ConfirmHost() {
  const req = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const { t } = useTranslation();
  const actionRef = useRef<HTMLButtonElement>(null);

  const open = req !== null;

  return (
    // AlertDialog intentionally does not close on overlay click — the choice is
    // explicit; closing is driven solely by the store via resolveConfirm.
    <AlertDialog.Root open={open}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm" />
        <AlertDialog.Content
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            resolveConfirm(false);
          }}
          onOpenAutoFocus={(e) => {
            if (req && !req.danger) {
              e.preventDefault();
              actionRef.current?.focus();
            }
          }}
          className="fixed left-1/2 top-1/2 z-[100] w-[400px] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-white/5 bg-[var(--bg-card)] p-6 shadow-2xl"
        >
          {req?.title ? (
            <AlertDialog.Title className="mb-2 text-base font-semibold text-[var(--text-primary)]">
              {req.title}
            </AlertDialog.Title>
          ) : (
            <AlertDialog.Title className="sr-only">{t('common.confirm')}</AlertDialog.Title>
          )}
          <AlertDialog.Description className="whitespace-pre-line text-sm text-[var(--text-secondary)]">
            {req?.message}
          </AlertDialog.Description>
          <div className="mt-5 flex justify-end gap-3">
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                onClick={() => resolveConfirm(false)}
                className="rounded-lg px-4 py-2 text-sm text-[var(--text-secondary)] transition-colors hover:bg-white/5"
              >
                {req?.cancelLabel ?? t('common.cancel')}
              </button>
            </AlertDialog.Cancel>
            <AlertDialog.Action asChild>
              <button
                ref={actionRef}
                type="button"
                onClick={() => resolveConfirm(true)}
                // Ink follows the fill, never text-white: the light theme rewrites .text-white to near-black
                // (!important), and a club's own colour may need dark ink. Red is 600, not 500 — white on
                // red-500 is 3.76:1, under AA for 14px text; on red-600 it is 4.83:1.
                className={cn(
                  'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                  req?.danger
                    ? 'text-on-fill bg-red-600 hover:bg-red-700'
                    : 'text-on-club bg-[var(--club-primary)] hover:opacity-90',
                )}
              >
                {req?.confirmLabel ?? t('common.confirm')}
              </button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
